<template class="content">
  <div v-if="history.isRequestPending || loading" class="loading">
    <MdIcon size="xxlg" name="loading" :spin="true" />
    Loading...
  </div>
  <ul v-else class="history-list">
    <li class="history-entry header">
      <div></div>
      <div>#</div>
      <div class="comment">Comment</div>
      <div>Author</div>
    </li>
    <li v-if="draft" class="history-entry header" @mouseover="over = draft" @mouseleave="over = null">
      <div>
        <MdIcon v-if="onScreen(draft.id)" size="sm" name="eye" title="Currently viewing" />
        <Button v-else-if="over == draft" title="View version" class="active" @click="$emit('view', { type: 'draft', item: draft })">
          <MdIcon size="sm" name="eye" />
        </Button>
        <Button
          v-if="over == draft && $hasPermission('prompts.write')"
          title="Activate this version"
          class="active"
          @click="$emit('activate', { type: 'draft', item: draft })"
        >
          <MdIcon size="sm" name="power" />
        </Button>
        <Button v-if="over == draft" title="Compare with current" class="active" @click="$emit('compare', { type: 'draft', item: draft })">
          <MdIcon size="sm" name="file-compare" />
        </Button>
      </div>
      <div title="Version"></div>
      <div title="User comment" class="comment draft">CURRENT DRAFT</div>
      <div>
        <div title="Author" class="author">{{ draft.updatedBy ? draft.updatedBy : draft.createdBy }}</div>
        <div title="Date crated" class="date">
          {{ new Date(draft.updatedAt ? draft.updatedAt : draft.createdAt).toLocaleString() }}
        </div>
      </div>
      <div>
        <Button
          v-if="over == draft && $hasPermission('prompts.write')"
          title="Discard draft"
          class="active"
          @click="$emit('delete', { type: 'draft', item: draft })"
        >
          <MdIcon size="sm" name="delete" />
        </Button>
      </div>
    </li>
    <li
      v-for="entry in history.entries"
      :key="entry.id"
      class="history-entry"
      :class="{ active: entry.isActive }"
      @mouseover="over = entry"
      @mouseleave="over = null"
    >
      <div>
        <div class="actions">
          <MdIcon v-if="onScreen(entry.id)" size="sm" name="eye" title="Currently viewing" />
          <Button v-else-if="over == entry" title="View this version" class="active" @click="$emit('view', { type: 'version', item: entry })">
            <MdIcon size="sm" name="eye" />
          </Button>
          <Button
            v-if="over == entry && !entry.isActive"
            title="Activate this version"
            class="active"
            @click="$emit('activate', { type: 'version', item: entry })"
          >
            <MdIcon size="sm" name="power" />
          </Button>
          <Button v-if="over == entry" title="Compare with current" class="active" @click="$emit('compare', { type: 'version', item: entry })">
            <MdIcon size="sm" name="file-compare" />
          </Button>
        </div>
      </div>
      <div title="Version">{{ entry.version }}</div>
      <div title="User comment" class="comment">
        <div v-if="editing && editing.id === entry.id" class="edit-mode">
          <TextField v-model="editing.comment" :multiline="true" placeholder="Comment" />
          <Button v-if="$hasPermission('prompts.write')" title="View version" class="active" @click="saveChanges(editing)">
            <MdIcon size="sm" name="check" />
          </Button>
          <Button title="View version" class="active" @click="editing = null">
            <MdIcon size="sm" name="close" />
          </Button>
        </div>
        <div v-else v-html="prepareComment(entry)"></div>
      </div>
      <div>
        <div :title="entry.updatedBy ? entry.updatedBy : entry.createdBy" class="author" >{{ entry.updatedBy ? entry.updatedBy : entry.createdBy }}</div>
        <div title="Date crated" class="date">{{ new Date(entry.updatedAt ? entry.updatedAt : entry.createdAt).toLocaleString() }}</div>
      </div>
      <div>
        <div v-if="over == entry">
          <Button title="Activate this version" class="active" @click="editing = JSON.parse(JSON.stringify(entry))">
            <MdIcon size="sm" name="pencil" />
          </Button>
          <Button v-if="!entry.isActive" title="Delete this version" class="active" @click="$emit('delete', { type: 'version', item: entry })">
            <MdIcon size="sm" name="delete" />
          </Button>
        </div>
      </div>
    </li>
  </ul>
</template>

<script>
import { mapState } from 'vuex';
import MdIcon from '@/components/common/MdIcon';
import Button from '@/components/common/Button';
import TextField from '@/components/common/TextField';

export default {
  components: { MdIcon, Button, TextField },
  props: {
    item: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chainId: this.item.id,
      over: null,
      editing: null
    };
  },
  computed: {
    ...mapState({
      history: s => s.prompts.history,
      preview: s => s.prompts.preview,
      draft: s => s.prompts.draft,
      currentView: s => s.prompts.currentView,
      current: s => s.prompts.current,
      currentViewKey: s => s.prompts.currentViewKey
    })
  },
  async created() {
    await this.$store.dispatch('prompts/fetchHistory', this.chainId ?? this.$route.params.id);
  },
  methods: {
    prepareComment(entry) {
      if (entry.comment) {
        return entry.comment.replace(/\r?\n/g, `<br>`);
      }
      return '';
    },
    async saveChanges() {
      const promise = this.$store.dispatch('prompts/editHistory', this.editing);
      this.editing = null;
      await promise;
    },
    onScreen(itemId) {
      return this.currentViewKey === itemId;
    }
  }
};
</script>

<style scoped lang="scss">
.content {
  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 40vh;
    font-size: 1.1rem;
  }

  .history-list {
    height: 100%;
    overflow-y: auto;
    list-style: none;

    li:nth-child(even) {
      background-color: var(--theme-surface);
    }

    .history-entry {
      display: grid;
      gap: 5px;
      font-size: 0.85rem;
      grid-template-columns: 60px 30px 1fr 150px auto;
      justify-content: left;
      align-items: center;
      justify-items: left;
      padding: 10px;
      border-bottom: 1px solid #ccc;
      min-height: 30px;

      &.active {
        background-color: var(--theme-primary-dark);
      }

      .actions {
        display: flex;
        gap: 5px;
      }

      .active {
        color: var(--theme-primary-light);
        padding: 0px;
      }

      .author {
        font-style: italic;
        font-size: 0.8rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 150px;
        display: block;
        justify-content: right;
      }
      .date {
        font-size: 0.7rem;
      }
      .comment {
        text-wrap: break-word;
        font-size: 0.85rem;
        line-height: 1.1rem;
        display: flex;
        width: 100%;

        .edit-mode {
          display: grid;
          grid-template-columns: minmax(350px, 1fr) auto auto;
          gap: 5px;
        }
      }
    }

    .header {
      font-weight: 700;
      font-size: 1rem;
      background-color: var(--theme-default);

      .draft {
        color: var(--theme-secondary);
      }
    }
  }
}
</style>
